import { useCallback, useEffect, useRef, useState } from 'react';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';

const POLL_MS = 2500;
const MAX_POLLS = 60;

// Async draft-generation lifecycle (Founder remediation §8): start job → poll
// status → reload when done, no browser refresh, no duplicate submissions.
export function useSuccGeneration(positionId, kind, isES, reload) {
  const x = (es, en) => (isES ? es : en);
  const [gen, setGen] = useState(false);
  const timer = useRef(null);
  const polls = useRef(0);

  const stop = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setGen(false);
  }, []);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get(`/client/succession/positions/${positionId}/generation/${kind}/status`);
      if (data.status === 'done') {
        stop();
        toast.success(x('Borrador generado correctamente. Revísalo y confírmalo.', 'Draft generated successfully. Review and confirm it.'));
        reload();
        return;
      }
      if (data.status === 'failed') {
        stop();
        toast.error(x('No fue posible completar la generación. Intenta nuevamente.', 'Generation could not be completed. Please try again.'));
        return;
      }
      if (data.status === 'running') {
        polls.current += 1;
        if (polls.current > MAX_POLLS) {
          stop();
          toast.error(x('No fue posible completar la generación. Intenta nuevamente.', 'Generation could not be completed. Please try again.'));
          return;
        }
        timer.current = setTimeout(poll, POLL_MS);
        return;
      }
      stop();
    } catch {
      polls.current += 1;
      if (polls.current > MAX_POLLS) { stop(); return; }
      timer.current = setTimeout(poll, POLL_MS);
    }
  }, [positionId, kind, reload, stop]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = useCallback(async () => {
    if (gen) return;
    setGen(true);
    polls.current = 0;
    try {
      await api.post(`/client/succession/positions/${positionId}/${kind}/generate`, { lang: isES ? 'es' : 'en' });
      timer.current = setTimeout(poll, POLL_MS);
    } catch (e) {
      stop();
      toast.error(e?.response?.data?.detail === 'succ_nothing_to_generate'
        ? x('Todo el contenido ya está confirmado. No hay nada por generar.', 'All content is already confirmed. There is nothing to generate.')
        : formatApiError(e));
    }
  }, [gen, positionId, kind, isES, poll, stop]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resume polling if a job is already running (e.g. after a page refresh).
  useEffect(() => {
    let cancelled = false;
    api.get(`/client/succession/positions/${positionId}/generation/${kind}/status`)
      .then(({ data }) => {
        if (!cancelled && data.status === 'running') {
          setGen(true);
          polls.current = 0;
          timer.current = setTimeout(poll, POLL_MS);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; if (timer.current) clearTimeout(timer.current); };
  }, [positionId, kind]); // eslint-disable-line react-hooks/exhaustive-deps

  return { gen, start };
}
